import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { includes, isBoolean, forEach, forOwn, remove } from 'lodash';
import Collapse from 'react-collapse';

import { LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import { AnalyticsService, VIRTUAL_PAGEVIEWS as PAGES } from '@sm360/widget-commons-general';
import { FormBuilder, ValidationService, ErrorsHandler } from '@sm360/widget-commons-forms';

import FORM_INPUTS from 'trade-in-360/components/OfficialTradeInForm/components/CarCondition/formInputs.js';
import 'trade-in-360/components/OfficialTradeInForm/components/CarCondition/CarCondition.scss';

class CarCondition extends Component {
    constructor(props) {
        super(props);

        this.state = {
            isValid: false,
            inputs: {
                hadAnAccident: '',
                needReparationOtherExplanation: '',
                clarifyDamage: '',
                airBagsOperational: '',
                modifiedParts: '',
                partsToReplace: [],
                replacedTires: '',
                replacedBrakes: '',
                anyScratches: '',
                damageExceeding3000: '',
                majorExteriorInteriorDamage: '',
                ...this.props.inputs,
            },
            formValidation: {
                hadAnAccident: false,
                needReparationOtherExplanation: true,
                clarifyDamage: true,
                airBagsOperational: false,
                modifiedParts: false,
                partsToReplace: false,
                replacedTires: false,
                replacedBrakes: false,
                anyScratches: false,
                damageExceeding3000: false,
                majorExteriorInteriorDamage: false,
            },
            errors: [],
        };

        this.buildInput = this.buildInput.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.sendForm = this.sendForm.bind(this);
    }

    componentDidMount() {
        AnalyticsService.virtualPageView(PAGES.OFFICIAL_TRADEIN_CONDITIONS_FORM());
    }

    validateForm() {
        const { formValidation } = this.state;
        forOwn(formValidation, (value, key) => {
            const fieldRequireValidation =
                (key === 'needReparationOtherExplanation' && includes(this.state.partsToReplace, 'other')) ||
                (key === 'clarifyDamage' && this.state.majorExteriorInteriorDamage) ||
                (key !== 'needReparationOtherExplanation' && key !== 'clarifyDamage');

            if (fieldRequireValidation) {
                const stateValue = this.state.inputs[key];
                formValidation[key] = isBoolean(stateValue) || ValidationService.validateIsNotEmpty(stateValue);
            }
        });

        this.setState({ formValidation });
    }

    handleSubmit(event) {
        event.preventDefault();
    }

    handleInputChange(value, name) {
        const nextState = this.state;

        switch (name) {
            case 'partsToReplace': {
                let currentSelection = nextState.inputs.partsToReplace;
                if (value === 'none') {
                    currentSelection = ['none'];
                } else {
                    remove(currentSelection, (selection) => {
                        return selection === 'none';
                    });
                    const index = currentSelection.findIndex((elem) => {
                        return elem === value;
                    });

                    if (index === -1) {
                        currentSelection.push(value);
                    } else {
                        currentSelection.splice(index, 1);
                    }
                }

                nextState.formValidation[name] = currentSelection.length > 0;
                nextState.inputs[name] = currentSelection;

                break;
            }
            case 'hadAnAccident': {
                forEach(FORM_INPUTS.ACCIDENT_QUESTION, (input) => {
                    if (input.name !== 'needReparationOtherExplanation') {
                        nextState.formValidation[input.name] = value === 'none' || value === 'lessThan3000';
                    }

                    nextState.inputs[input.name] = value !== '3000AndMore' ? nextState.inputs[input.name] : '';
                });

                nextState.inputs[name] = value;
                nextState.formValidation[name] = true;
                break;
            }
            default: {
                nextState.inputs[name] = value;
                nextState.formValidation[name] = true;
                break;
            }
        }

        this.setState(nextState);
    }

    sendForm(event) {
        event.preventDefault();

        if (!ValidationService.validateForm(this.state.formValidation)) {
            this.setState({
                errors: [
                    {
                        message: LocaleService.t('TRADE_IN.VEHICLE_INFORMATION.ERROR'),
                        code: 'error.missing_inputs',
                        group: 'carInformation',
                    },
                ],
            });
            const nav = WindowProxy.get().document.getElementsByClassName('officialTradeInForm')
                ? WindowProxy.get().document.getElementsByClassName('officialTradeInForm')[0]
                : undefined;
            if (nav) {
                nav.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
            return;
        }

        this.setState({
            errors: [],
            isValid: true,
        });

        this.props.onFormSubmit(this.state.inputs);
    }

    buildInput(input) {
        let isRequired = true;
        const options =
            input.options &&
            input.options.map((option) => {
                if (input.inputType === 'radio') {
                    return {
                        ...option,
                        label: <span>{LocaleService.t(option.transKey)}</span>,
                        isRequired: true,
                    };
                } else if (input.inputType === 'checkbox') {
                    return {
                        ...option,
                        isSelected: includes(this.state.inputs.partsToReplace, option.value),
                        label: LocaleService.t(option.transKey),
                    };
                }

                return {
                    ...option,
                    label: option.transKey && LocaleService.t(option.transKey),
                };
            });

        if (input.name === 'needReparationOtherExplanation' || input.name === 'clarifyAccident') {
            isRequired = false;
        }

        return (
            <div key={input.name}>
                <FormBuilder
                    inputParams={{
                        ...input,
                        label: LocaleService.t(input.transKey),
                        options,
                        value: this.state.inputs[input.name],
                        isRequired,
                    }}
                    onChange={this.handleInputChange}
                />
                {input.subQuestions && (
                    <div>
                        <Collapse
                            isOpened={this.state.inputs[input.name] === input.showSubQuestionsValue}
                            springConfig={{ stiffness: 200, damping: 30 }}
                        >
                            <div>{input.subQuestions.map(this.buildInput)}</div>
                        </Collapse>
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { isOpen } = this.props;
        const { isValid, errors } = this.state;
        const inputsCarQuestion = FORM_INPUTS.CAR_CONDITION_QUESTION.map(this.buildInput);
        const inputsReparation =
            this.state.inputs.partsToReplace.length >= 1 &&
            this.state.inputs.partsToReplace[0] !== 'none' &&
            FORM_INPUTS.EXPLAIN_REPARATION.map(this.buildInput);
        const inputsCarConditions = FORM_INPUTS.CAR_CONDITION.map(this.buildInput);
        const inputsExteriorDommage =
            this.state.inputs.majorExteriorInteriorDamage && FORM_INPUTS.VEHICLE_DAMAGE.map(this.buildInput);

        const formClass = isOpen
            ? 'officialTradeIn__section carCondition is-open'
            : `officialTradeIn__section carCondition is-closed ${isValid && 'is-valid'}`;

        const errorBlock = !!errors.length && <ErrorsHandler errors={errors} />;

        const carConditionForm = isOpen && (
            <form onSubmit={this.handleSubmit}>
                <div className="clearfix">
                    {inputsCarQuestion}
                    {inputsReparation}
                    {inputsCarConditions}
                    {inputsExteriorDommage}
                </div>
                <button type="button" className="sr-button-1" onClick={this.sendForm}>
                    {LocaleService.t('TRADE_IN.BUTTONS.DONE')}
                </button>
            </form>
        );
        return (
            <div className={formClass}>
                <h3 className="sr-subtitle-2">{LocaleService.t('TRADE_IN.VEHICLE_INFORMATION.TITLE')}</h3>
                {errorBlock}
                {carConditionForm}
            </div>
        );
    }
}

CarCondition.propTypes = {
    isOpen: PropTypes.bool,
    inputs: PropTypes.objectOf({}),
    onFormSubmit: PropTypes.func.isRequired,
};

CarCondition.defaultProps = {
    isOpen: true,
    inputs: {},
};

CarCondition.displayName = 'CarCondition';

export default CarCondition;
